/**
 * What the Orders list shows as an order's number (kanban 2676b86b).
 *
 * Sage assigns the order number when an order is submitted, so a draft
 * (or a failed submission, which is still pre-Sage) has none. People
 * asked for an order number used to have to leave the list to find out
 * whether one existed; the row now says so in words instead of showing
 * a blank.
 */
import type { OrderRecord } from '../../types/orders';

export type OrderNumberKind = 'numbered' | 'draft' | 'pending';

export interface OrderNumberLabel {
  kind: OrderNumberKind;
  /** Text rendered in the chip. */
  text: string;
  /** Hover title explaining the chip. */
  title: string;
}

const PRE_SAGE_STATUSES: ReadonlySet<OrderRecord['status']> = new Set([
  'draft',
  'submission_failed',
]);

export function orderNumberLabel(
  order: Pick<OrderRecord, 'sage_order_no' | 'status'>,
): OrderNumberLabel {
  const sageNo = order.sage_order_no?.trim();
  if (sageNo) {
    return { kind: 'numbered', text: sageNo, title: `Sage order number ${sageNo}` };
  }
  if (PRE_SAGE_STATUSES.has(order.status)) {
    return {
      kind: 'draft',
      text: 'Draft, no number yet',
      title: 'Still a draft. Sage assigns the order number when the order is submitted.',
    };
  }
  return {
    kind: 'pending',
    text: 'No Sage number yet',
    title: 'Submitted, but Sage has not reported an order number for this order yet.',
  };
}
